import { OktaAuth, OktaAuthOptions, AuthState, OAuthResponseType } from '@okta/okta-auth-js';
import * as Sentry from "@sentry/nextjs";


let oktaAuth: OktaAuth | null = null;
let cachedAccessToken: string | null = null;
const oktaResponseTypes: OAuthResponseType[] = ['token', 'id_token'];

export const TWELVE_HOURS = 12 * 60 * 60; // 12 hours in seconds
export const POST_LOGIN_TIMEOUT = TWELVE_HOURS * 1000; // 12 hours in milliseconds
export const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export enum AuthStatus {
  NotLoggedIn,
  LoggedIn,
  LoggingOut,
  LoggedOut
}

export const initializeAuth = async (config: OktaAuthOptions): Promise<AuthState | null> => {
  if (!oktaAuth) {
    oktaAuth = new OktaAuth(config);
    await oktaAuth.start();
    return await Promise.resolve(oktaAuth.authStateManager.getAuthState());
  }
  return await Promise.resolve(oktaAuth.authStateManager.getAuthState());
};

export const getOktaAuth = (): OktaAuth | null => {
  return oktaAuth;
};

export const subscribeToAuthStateChanges = (callback: (authState: AuthState) => void) => {
  const auth = getOktaAuth();
  if (auth) {
    auth.authStateManager.subscribe(callback);
  }
};

export const unsubscribeFromAuthStateChanges = (callback: (authState: AuthState) => void) => {
  const auth = getOktaAuth();
  if (auth) {
    auth.authStateManager.unsubscribe(callback);
  }
};

export const getAuthState = async (): Promise<AuthState | null> => {
  const auth = getOktaAuth();
  return auth ? await Promise.resolve(auth.authStateManager.getAuthState()) : null;
};

export const handleLogin = async () => {
  const auth = getOktaAuth();
  if (auth) {
    await auth.signInWithRedirect({
      responseType: oktaResponseTypes,
      maxAge: TWELVE_HOURS,
    });
  } else {
    console.error('OktaAuth not initialized');
  }
};

export const handleLogout = async () => {
  const auth = getOktaAuth();
  if (auth) {
    sessionStorage.setItem('isLoggingOut', 'true');
    clearCachedAccessToken();
    await auth.signOut({
      postLogoutRedirectUri: auth.options.postLogoutRedirectUri
    });
  } else {
    console.error('OktaAuth not initialized');
  }
};

export const getAccessToken = async (): Promise<string | null> => {
  const auth = getOktaAuth();
  if (auth) {
    try {
      cachedAccessToken = (await auth.getOrRenewAccessToken()) || null;
      return cachedAccessToken;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting access token:', error);
      return null;
    }
  } else {
    console.error('OktaAuth not initialized');
  }
  return null;
};

export const clearCachedAccessToken = () => {
  cachedAccessToken = null;
};

export const isLoginRedirect = (): boolean => {
  const auth = getOktaAuth();
  return auth ? auth.isLoginRedirect() : false;
};

export const handleLoginRedirect = async (): Promise<void> => {
  const auth = getOktaAuth();
  if (auth) {
    await auth.handleLoginRedirect();
    await updateCachedAccessToken();
  } else {
    console.error('OktaAuth not initialized');
  }
};

export const forceReLogin = async () => {
  const auth = getOktaAuth();
  if (auth) {
    try {
      await auth.signOut({
        postLogoutRedirectUri: auth.options.postLogoutRedirectUri
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error during sign out:', error);
    }
    
    try {
      await auth.signInWithRedirect({
        prompt: 'login',
        responseType: oktaResponseTypes,
        maxAge: TWELVE_HOURS,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error during sign in redirect:', error);
      throw error;
    }
  }
};

export const renewAccessToken = async (): Promise<void> => {
  const auth = getOktaAuth();
  if (auth && auth.authStateManager.getAuthState()?.isAuthenticated) {
    const accessToken = await auth.tokenManager.get('accessToken');
    if (accessToken && auth.tokenManager.hasExpired(accessToken)) {
      await auth.tokenManager.renew('accessToken');
    }
  }
};

export const checkAuthState = async (
  authStatus: AuthStatus,
  isUserIdle: () => boolean,
  lastAuthTime: number,
  isReLoginAttempted: boolean,
  setAuthState: (state: AuthState | null) => void,
  setIsReLoginAttempted: (value: boolean) => void
): Promise<void> => {
  if (authStatus !== AuthStatus.LoggedOut && authStatus !== AuthStatus.LoggingOut) {
    const currentAuthState = await getAuthState();
    setAuthState(currentAuthState);

    if (currentAuthState?.isAuthenticated) {
      const userIsIdle = isUserIdle();
      const timeSinceLastAuth = Date.now() - lastAuthTime;

      if (userIsIdle || timeSinceLastAuth > POST_LOGIN_TIMEOUT) {
        console.log('User is idle or session expired. Forcing re-login.');
        await forceReLogin();
      } else {
        await renewAccessToken();
      }
    } else if (!isReLoginAttempted) {
      console.log('User is not authenticated. Attempting re-login.');
      setIsReLoginAttempted(true);
      await forceReLogin();
    } else {
      console.log('OktaAuth not initialized or user has logged out');
    }
  }
};

export const updateCachedAccessToken = async () => {
  const auth = getOktaAuth();
  if (auth) {
    cachedAccessToken = await Promise.resolve(auth.getAccessToken() || null);
  }
};